import React, { useLayoutEffect, useEffect, useRef, useCallback } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

var _document$body, _document$body$style;

var isArray = Array.isArray;
var isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
/** Whether the environment support -webkit-line-clamp. */

var isSupportNativeEllipsis = isBrowser && typeof ((_document$body = document.body) == null ? void 0 : (_document$body$style = _document$body.style) == null ? void 0 : _document$body$style.webkitLineClamp) !== 'undefined';

/**
 * Merge classNames by conditions.
 * @param {ClassNameObjectType} args The className to merge
 */

function classNames () {
  var result = [];

  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }

  args.forEach(function (item) {
    if (typeof item === 'string') {
      result.push(item);
    } else if (isArray(item)) {
      result = result.concat(item);
    } else if (item) {
      Object.keys(item).forEach(function (key) {
        if (item[key]) {
          result.push(key);
        }
      });
    }
  });
  return result.join(' ');
}

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".__react-ellipsis-native.ellipsis {\n  display: -webkit-box;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  word-break: break-word;\n  -webkit-box-orient: vertical;\n}\n";
styleInject(css_248z);

function NativeEllipsis(_ref) {
  var text = _ref.text,
      maxLine = _ref.maxLine,
      ellipsis = _ref.ellipsis,
      dangerouslyUseInnerHTML = _ref.dangerouslyUseInnerHTML;
  return React.createElement("div", {
    className: classNames('__react-ellipsis-native', {
      ellipsis: ellipsis
    }),
    style: {
      WebkitLineClamp: maxLine
    }
  }, dangerouslyUseInnerHTML ? React.createElement("span", {
    dangerouslySetInnerHTML: {
      __html: text
    }
  }) : React.createElement("span", null, text));
}

var NativeEllipsis$1 = /*#__PURE__*/React.memo(NativeEllipsis);

var useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Return the current style of an element.
 * @param {HTMLElement} element The element to compute.
 * @param {string} prop The style property.
 * @returns {number}
 */
function computeStyle(element, prop) {
  if (!window.getComputedStyle) {
    window.getComputedStyle = function (el) {
      this.getPropertyValue = function (key) {
        var regex = /(-([a-z]){1})/g;
        if (key === 'float') key = 'styleFloat';

        if (regex.test(key)) {
          key = key.replace(regex, function () {
            return arguments[2].toUpperCase();
          });
        }

        return el.currentStyle && el.currentStyle[key] ? el.currentStyle[key] : null;
      };

      return this;
    };
  }

  return window.getComputedStyle(element, null).getPropertyValue(prop);
}
/**
 * Return the line-height of an element.
 * @param {HTMLElement} element The element to get line-height.
 * @returns {number}
 */


function getLineHeight(element) {
  var lineHeight = computeStyle(element, 'line-height');

  if (lineHeight === 'normal') {
    // Create a temp element to get line-height
    var dom = document.createElement('span');
    dom.style.width = '300px';
    dom.style.position = 'absolute';
    dom.style.visibility = 'hidden';
    dom.innerText = 'test';
    element.appendChild(dom);
    var height = dom.clientHeight;
    element.removeChild(dom); // Compatible some browser

    return height * 1.1;
  }

  return parseFloat(lineHeight);
}

function createSpan(text) {
  var span = document.createElement('span');
  span.className = '__react-ellipsis-js-content-text';
  span.textContent = text;
  return span;
}

function wrapTextChildNodesWithSpan(node) {
  if (!isBrowser) {
    return;
  }

  if (node.nodeType === Node.TEXT_NODE) {
    if (node.textContent) {
      var _node$parentNode;

      (_node$parentNode = node.parentNode) == null ? void 0 : _node$parentNode.replaceChild(createSpan(node.textContent), node);
    }
  } else {
    node.childNodes.forEach(function (item) {
      wrapTextChildNodesWithSpan(item);
    });
  }
}
function getElementHeight(element) {
  return element.getBoundingClientRect().height;
}

function JsEllipsis(props) {
  var text = props.text,
      dangerouslyUseInnerHTML = props.dangerouslyUseInnerHTML,
      _props$maxLine = props.maxLine,
      maxLine = _props$maxLine === void 0 ? 1 : _props$maxLine,
      visibleLine = props.visibleLine,
      maxHeight = props.maxHeight,
      visibleHeight = props.visibleHeight,
      ellipsis = props.ellipsis,
      ellipsisNode = props.ellipsisNode,
      _props$endExcludes = props.endExcludes,
      endExcludes = _props$endExcludes === void 0 ? [] : _props$endExcludes,
      reflowOnResize = props.reflowOnResize,
      onReflow = props.onReflow,
      onEllipsisClick = props.onEllipsisClick; // default visibleLine equal to maxLine.

  var _visibleLine = typeof visibleLine === 'undefined' ? maxLine : visibleLine;

  var observerRef = useRef(null);
  var truncating = useRef(false);
  var ref = useRef(null);
  var textRef = useRef(null);
  var ellipsisRef = useRef(null);
  var lineHeightRef = useRef(0);

  function handleOnReflow(ellipsis, result) {
    if (onReflow && typeof onReflow === 'function') {
      onReflow(ellipsis, result);
    }
  }

  var reflow = useCallback(function () {
    if (!ref.current || !textRef.current || !ellipsisRef.current || truncating.current) {
      return;
    }

    ellipsisRef.current.style.display = 'none';

    if (dangerouslyUseInnerHTML) {
      textRef.current.innerHTML = text;
    } else {
      textRef.current.innerText = text;
    }

    if (!ellipsis) {
      return;
    }

    var max = typeof maxHeight === 'undefined' ? lineHeightRef.current * maxLine : maxHeight; // desired visible height of container.

    var visibleMax = typeof visibleHeight === 'undefined' ? typeof maxHeight === 'undefined' ? lineHeightRef.current * _visibleLine : maxHeight : visibleHeight;
    var height = getElementHeight(ref.current); // content will be truncated if the content's height bigger than Math.max(max, visibleMax).

    if (height <= Math.max(max, visibleMax)) {
      handleOnReflow(false, text);
      return;
    }

    truncating.current = true;
    ellipsisRef.current.style.display = 'inline';

    if (dangerouslyUseInnerHTML) {
      // wrap the text children node with span element.
      wrapTextChildNodesWithSpan(textRef.current);
      truncateHTML(ref.current, textRef.current, visibleMax);
    } else {
      truncateText(ref.current, textRef.current, visibleMax);
    }

    truncating.current = false;
  }, [text, ellipsis, dangerouslyUseInnerHTML, maxLine, visibleLine, maxHeight, visibleHeight, ellipsisNode, endExcludes]);

  function truncateText(container, textContainer, max) {
    var text = textContainer.textContent || '';
    var currentText = '';
    var l = 0;
    var r = text.length; // Binary truncate text until get the max limit fragment of text.

    while (l < r) {
      var m = Math.floor((l + r) / 2);

      if (l === m) {
        break;
      }

      var temp = text.slice(l, m);
      textContainer.innerText = currentText + temp;
      var height = container.clientHeight;

      if (height > max) {
        r = m;
      } else {
        currentText += temp;
        l = m;
      }
    } // Remove the exclude char at the end of the content.


    while (endExcludes.includes(currentText[currentText.length - 1])) {
      currentText = currentText.slice(0, -1);
    }

    textContainer.innerText = currentText; // Callback after reflow.

    handleOnReflow(true, currentText);
  }

  function truncateHTML(container, textContainer, max) {
    // only enter this function when container overflow.
    var children = textContainer.childNodes;

    if (children.length === 1) {
      var node = children[0];

      if (node.nodeType === Node.TEXT_NODE) {
        truncateText(container, textContainer, max);
      } else {
        var html = node.innerHTML; // clear content to determine whether the empty node can be placed.

        node.innerHTML = '';
        var height = container.clientHeight;

        if (height > max) {
          // return after remove the node, if overflow with empty node.
          textContainer.removeChild(node);
          handleOnReflow(true, textContainer.innerHTML);
          return;
        }

        node.innerHTML = html; // recursive truncate node

        truncateHTML(container, node, max);
      }
    } else {
      var nodes = [].slice.call(children);
      textContainer.innerHTML = '';
      var i = 0; // find the critical node

      while (i < nodes.length) {
        // clone the empty node to determine whether the empty node can be placed.
        var _node = nodes[i].cloneNode();

        textContainer.appendChild(_node);

        if (container.clientHeight > max) {
          // return after remove the node, if overflow with empty node.
          textContainer.removeChild(_node);
          handleOnReflow(true, textContainer.innerHTML);
          return;
        }

        textContainer.removeChild(_node);
        textContainer.appendChild(nodes[i]);
        var _height = container.clientHeight;

        if (_height > max) {
          break;
        }

        i++;
      }

      if (textContainer.childNodes[i]) {
        // truncate the critical node
        truncateHTML(container, textContainer.childNodes[i], max);
      }
    }
  } // Call truncate function to reflow when the main props change.


  useIsomorphicLayoutEffect(function () {
    if (ref.current) {
      lineHeightRef.current = getLineHeight(ref.current);
    }

    reflow();
  }, [reflow]); // Observe resize event of container if reflowOnResize is true.

  useIsomorphicLayoutEffect(function () {
    var dom = ref.current;

    if (dom && reflowOnResize) {
      if (!observerRef.current && ellipsis) {
        observerRef.current = new ResizeObserver(reflow);
        observerRef.current.observe(dom);
      }
    }

    return function () {
      if (observerRef.current && dom) {
        var _observerRef$current;

        // Remove observer when component unmounted.
        (_observerRef$current = observerRef.current) == null ? void 0 : _observerRef$current.unobserve(dom);
        observerRef.current = null;
      }
    };
  }, [ellipsis]); // callback of ellipsis click event

  function handleEllipsisClick() {
    if (onEllipsisClick && typeof onEllipsisClick === 'function') {
      onEllipsisClick();
    }
  }

  return React.createElement("div", {
    ref: ref,
    className: "__react-ellipsis-js"
  }, React.createElement("span", {
    ref: textRef,
    className: "__react-ellipsis-js-content"
  }), React.createElement("span", {
    ref: ellipsisRef,
    className: "__react-ellipsis-js-ellipsis",
    onClick: handleEllipsisClick
  }, ellipsisNode));
}

var JsEllipsis$1 = /*#__PURE__*/React.memo(JsEllipsis);

function ReactEllipsis(props) {
  var className = props.className,
      _props$ellipsis = props.ellipsis,
      ellipsis = _props$ellipsis === void 0 ? true : _props$ellipsis,
      text = props.text,
      _props$dangerouslyUse = props.dangerouslyUseInnerHTML,
      dangerouslyUseInnerHTML = _props$dangerouslyUse === void 0 ? false : _props$dangerouslyUse,
      _props$maxLine = props.maxLine,
      maxLine = _props$maxLine === void 0 ? 1 : _props$maxLine,
      visibleLine = props.visibleLine,
      maxHeight = props.maxHeight,
      visibleHeight = props.visibleHeight,
      _props$ellipsisNode = props.ellipsisNode,
      ellipsisNode = _props$ellipsisNode === void 0 ? '…' : _props$ellipsisNode,
      _props$endExcludes = props.endExcludes,
      endExcludes = _props$endExcludes === void 0 ? [] : _props$endExcludes,
      _props$reflowOnResize = props.reflowOnResize,
      reflowOnResize = _props$reflowOnResize === void 0 ? false : _props$reflowOnResize,
      onReflow = props.onReflow,
      onEllipsisClick = props.onEllipsisClick;
  var useNativeEllipsis = isSupportNativeEllipsis && visibleLine === undefined && maxHeight === undefined && visibleHeight === undefined && ellipsisNode === '…' && endExcludes.length === 0 && !onReflow && !onEllipsisClick;
  return React.createElement("div", {
    className: classNames('__react-ellipsis', className)
  }, useNativeEllipsis ? React.createElement(NativeEllipsis$1, {
    ellipsis: ellipsis,
    dangerouslyUseInnerHTML: dangerouslyUseInnerHTML,
    text: text,
    maxLine: maxLine
  }) : React.createElement(JsEllipsis$1, {
    text: text,
    dangerouslyUseInnerHTML: dangerouslyUseInnerHTML,
    maxLine: maxLine,
    visibleLine: visibleLine,
    maxHeight: maxHeight,
    visibleHeight: visibleHeight,
    ellipsis: ellipsis,
    ellipsisNode: ellipsisNode,
    endExcludes: endExcludes,
    reflowOnResize: reflowOnResize,
    onReflow: onReflow,
    onEllipsisClick: onEllipsisClick
  }));
}

var index = /*#__PURE__*/React.memo(ReactEllipsis);

export default index;
//# sourceMappingURL=ellipsis.esm.js.map
